import type { PathAnchor, PathElement, CanvasElement } from '../types';

export function anchorsToPathData(anchors: PathAnchor[], closed: boolean): string {
  if (anchors.length === 0) return '';
  if (anchors.length === 1) return `M ${anchors[0].x} ${anchors[0].y}`;

  let d = `M ${anchors[0].x} ${anchors[0].y}`;

  for (let i = 1; i < anchors.length; i++) {
    const prev = anchors[i - 1];
    const curr = anchors[i];
    const hasHandleOut = prev.handleOut;
    const hasHandleIn = curr.handleIn;

    if (hasHandleOut && hasHandleIn) {
      d += ` C ${hasHandleOut.x} ${hasHandleOut.y} ${hasHandleIn.x} ${hasHandleIn.y} ${curr.x} ${curr.y}`;
    } else if (hasHandleOut) {
      d += ` Q ${hasHandleOut.x} ${hasHandleOut.y} ${curr.x} ${curr.y}`;
    } else if (hasHandleIn) {
      d += ` Q ${hasHandleIn.x} ${hasHandleIn.y} ${curr.x} ${curr.y}`;
    } else {
      d += ` L ${curr.x} ${curr.y}`;
    }
  }

  if (closed && anchors.length > 2) {
    const last = anchors[anchors.length - 1];
    const first = anchors[0];
    const hasHandleOut = last.handleOut;
    const hasHandleIn = first.handleIn;

    if (hasHandleOut && hasHandleIn) {
      d += ` C ${hasHandleOut.x} ${hasHandleOut.y} ${hasHandleIn.x} ${hasHandleIn.y} ${first.x} ${first.y}`;
    } else if (hasHandleOut) {
      d += ` Q ${hasHandleOut.x} ${hasHandleOut.y} ${first.x} ${first.y}`;
    } else if (hasHandleIn) {
      d += ` Q ${hasHandleIn.x} ${hasHandleIn.y} ${first.x} ${first.y}`;
    }
    d += ' Z';
  }

  return d;
}

export function calculatePathBounds(anchors: PathAnchor[]): { x: number; y: number; width: number; height: number } {
  if (anchors.length === 0) return { x: 0, y: 0, width: 0, height: 0 };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const a of anchors) {
    if (a.x < minX) minX = a.x;
    if (a.y < minY) minY = a.y;
    if (a.x > maxX) maxX = a.x;
    if (a.y > maxY) maxY = a.y;

    if (a.handleIn) {
      if (a.handleIn.x < minX) minX = a.handleIn.x;
      if (a.handleIn.y < minY) minY = a.handleIn.y;
      if (a.handleIn.x > maxX) maxX = a.handleIn.x;
      if (a.handleIn.y > maxY) maxY = a.handleIn.y;
    }
    if (a.handleOut) {
      if (a.handleOut.x < minX) minX = a.handleOut.x;
      if (a.handleOut.y < minY) minY = a.handleOut.y;
      if (a.handleOut.x > maxX) maxX = a.handleOut.x;
      if (a.handleOut.y > maxY) maxY = a.handleOut.y;
    }
  }

  return {
    x: minX,
    y: minY,
    width: Math.max(maxX - minX, 1),
    height: Math.max(maxY - minY, 1),
  };
}

export function recalcPathElement(
  anchors: PathAnchor[],
  _currentX: number,
  _currentY: number,
  closed: boolean
): { pathData: string; x: number; y: number; width: number; height: number; anchors: PathAnchor[] } {
  const bounds = calculatePathBounds(anchors);
  const normalized: PathAnchor[] = anchors.map((a) => ({
    x: a.x - bounds.x,
    y: a.y - bounds.y,
    handleIn: a.handleIn ? { x: a.handleIn.x - bounds.x, y: a.handleIn.y - bounds.y } : undefined,
    handleOut: a.handleOut ? { x: a.handleOut.x - bounds.x, y: a.handleOut.y - bounds.y } : undefined,
  }));

  return {
    pathData: anchorsToPathData(normalized, closed),
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    anchors: normalized,
  };
}

export function previewPathData(anchors: PathAnchor[], cursorX: number, cursorY: number): string {
  if (anchors.length === 0) return '';
  const base = anchorsToPathData(anchors, false);
  const last = anchors[anchors.length - 1];
  if (last.handleOut) {
    return base + ` Q ${last.handleOut.x} ${last.handleOut.y} ${cursorX} ${cursorY}`;
  }
  return base + ` L ${cursorX} ${cursorY}`;
}

export function resolveTextPathData(
  pathElementId: string,
  elements: CanvasElement[]
): string | null {
  const el = elements.find((e) => e.id === pathElementId);
  if (!el || el.type !== 'path') return null;
  return (el as PathElement).pathData || null;
}
